import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Briefcase, GraduationCap, Rocket, Award } from 'lucide-react';

const accent = '#d4af37';
const navy = '#1a2332';

// Build a unified, chronological-ish set of milestones from verified data.
const milestones = [
  { type: 'role', icon: Briefcase, title: 'Director of Operations', org: 'TekWissen Group', period: 'June 2026 – Present',
    detail: 'Leads global delivery operations, organizational strategy, and operational excellence across multiple geographies.' },
  { type: 'product', icon: Rocket, title: 'Founder — RecruiterIQ', org: 'therecruiteriq.com', period: 'Technology & AI',
    detail: 'Founded RecruiterIQ, an AI-powered recruitment intelligence and talent sourcing platform trusted by 10,000+ recruiters worldwide.' },
  { type: 'role', icon: Briefcase, title: 'Associate Director', org: 'TekWissen Group', period: 'January 2023 – May 2026',
    detail: 'Oversaw personnel management for 400+ team members, strategic systems, and Fortune 500 client partnerships.' },
  { type: 'role', icon: Briefcase, title: 'Senior Program Manager', org: 'TekWissen Group', period: 'September 2021 – January 2023',
    detail: 'Led full-cycle recruitment across US, Canada, EMEA, UK and India, improving team efficiency by 35%.' },
  { type: 'role', icon: Briefcase, title: 'Deputy Manager — Operations & Staffing', org: 'TekWissen Group', period: 'July 2019 – August 2021',
    detail: 'Managed delivery operations across six global markets with a team of 200+ members.' },
  { type: 'education', icon: GraduationCap, title: 'MBA', org: 'NIBM', period: 'Education',
    detail: 'Master of Business Administration — the foundation of a business-first approach to operations and talent.' },
];

const awards = [
  { year: '2025', title: 'Top 100 Influencing Leaders', org: 'Fox Story India' },
  { year: '2025', title: 'Top Staffing Leaders to Watch', org: 'Candidate.ly' },
  { year: '2024', title: 'Top Staffing Leaders to Watch', org: 'Candidate.ly' },
  { year: '2023', title: 'Top 10 Growing Influential Leaders', org: 'FoxStory' },
  { year: '2023', title: '40 Under 40 Dynamic Leaders', org: 'HRAI' },
  { year: '2023', title: 'Top 100 Influential Leaders', org: 'Leadify India' },
  { year: '2022', title: 'Top 100 Great People Managers', org: 'Forbes India' },
  { year: '2021', title: "World's Best Recruiters", org: 'RChilli' },
];

const CareerPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="career-page">
      <SEOHead
        title="Career Timeline | Venkat Swaroop"
        description="The career timeline of Venkat Swaroop — operational leadership roles at TekWissen Group, founding RecruiterIQ, education, and industry awards."
        url="https://venkatswaroop.in/career"
      />
      <Header />

      <section className="pt-32 pb-16 text-white" style={{ backgroundColor: navy }}>
        <div className="container mx-auto px-6 text-center">
          <h1 className="mb-4 font-serif text-4xl font-bold sm:text-5xl">Career Timeline</h1>
          <div className="mx-auto h-1 w-24 rounded" style={{ backgroundColor: accent }}></div>
          <p className="mx-auto mt-6 max-w-2xl text-base text-white/80 md:text-lg">
            Milestones across operations leadership, technology, education, and recognition.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto max-w-3xl px-6">
          <div className="relative border-l-2 pl-8" style={{ borderColor: `${accent}55` }}>
            {milestones.map((m, i) => (
              <div key={i} className="relative mb-10" data-testid={`milestone-${i}`}>
                <span
                  className="absolute -left-[42px] flex h-8 w-8 items-center justify-center rounded-full border-4 border-white shadow"
                  style={{ backgroundColor: accent }}
                >
                  <m.icon className="h-4 w-4 text-white" />
                </span>
                <p className="text-sm font-semibold" style={{ color: accent }}>{m.period}</p>
                <h3 className="font-serif text-xl font-bold" style={{ color: navy }}>{m.title}</h3>
                <p className="mb-1 text-sm font-medium text-gray-500">{m.org}</p>
                <p className="text-base leading-relaxed text-gray-700">{m.detail}</p>
              </div>
            ))}
          </div>

          {/* Awards */}
          <div className="mt-12">
            <div className="mb-6 flex items-center gap-3">
              <span className="inline-flex rounded-full p-2" style={{ backgroundColor: `${accent}20` }}>
                <Award className="h-6 w-6" style={{ color: accent }} />
              </span>
              <h2 className="font-serif text-2xl font-bold md:text-3xl" style={{ color: navy }}>Awards &amp; Recognition</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {awards.map((a, i) => (
                <div key={i} className="rounded-xl border p-4" style={{ borderColor: `${accent}40` }}>
                  <p className="text-sm font-semibold" style={{ color: accent }}>{a.year}</p>
                  <p className="font-semibold" style={{ color: navy }}>{a.title}</p>
                  <p className="text-sm text-gray-500">{a.org}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 text-center">
            <RouterLink
              to="/biography"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold transition-all hover:scale-105"
              style={{ backgroundColor: accent, color: navy }}
            >
              Read the full biography
            </RouterLink>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CareerPage;
